import { makeStyles } from "@mui/styles";
import { getTopTransmissionProgressReport } from "app/services/userActivityLog/userActivityLog.service";
import { setTopUserTransmissionProgressData } from "app/store/userActivityLog/slice/userActivityLogSlice";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { TOP_USER_TRANSMISSION_OFFSET_WIDTH } from "static/constants";
import { generateDateRangeArray, getStartOfPastMonth } from "utils/date";
import { showErrorToast } from "utils/toast";

const useTopTransmissionReportLog = () => {
  const dispatch = useDispatch();

  const [isFetching, setIsFetching] = useState(false);

  const { topUserTransmissionProgressData } = useSelector(
    (state) => state.userActivityLog
  );

  const screenWidth = window.innerWidth - TOP_USER_TRANSMISSION_OFFSET_WIDTH;

  const useStyles = makeStyles(() => ({
    topUserTransmissionContainer: {
      maxWidth: screenWidth + "px",
      overflowX: "auto",
      whiteSpace: "nowrap",
      padding: "8px",
      border: "1px solid #ccc",
      borderRadius: "4px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },

    topUserTransmissionChart: {
      display: "inline-block",
      minWidth: "33.33%",
      margin: "16px",
      borderRadius: "4px",
    },
  }));

  useEffect(() => {
    if (topUserTransmissionProgressData?.dailyReportCount?.length) {
      return;
    }

    getTopUserProgressReport();
  }, []);

  const getTopUserProgressReport = async () => {
    try {
      setIsFetching(true);

      const response = await getTopTransmissionProgressReport();
      if (response.error) {
        throw response.error;
      }

      if (response.data) {
        generateChartData(response.data);
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      setIsFetching(false);
    }
  };

  const generateChartData = (topTransmissionProgressData) => {
    if (!topTransmissionProgressData) {
      return;
    }

    const { dateArray, dailyReportCount } = prepareChartData(
      topTransmissionProgressData
    );

    const chartData = {
      labels: dateArray,
      dailyReportCount,
    };

    dispatch(setTopUserTransmissionProgressData(chartData));
  };

  const prepareChartData = (userTransmissionProgressData) => {
    const dateArray = generateDateRangeArray(getStartOfPastMonth(1).toDate());
    const dailyReportCount = [];
    userTransmissionProgressData.forEach((userTransmission) => {
      const { processedReportCount, name, email, count } = userTransmission;
      const chartData = getReportCountArrayBasedOnDate(
        dateArray,
        processedReportCount
      );

      const maxCount = Math.max(...chartData.filter((value) => value !== null));
      dailyReportCount.push({ name, email, maxCount, count, chartData });
    });

    return { dateArray, dailyReportCount };
  };

  const getReportCountArrayBasedOnDate = (dates, reportCounts) => {
    const reportCountArrayBasedOnDate = Array(dates.length).fill(null);
    reportCounts.forEach((report) => {
      const indexOfReportCount = dates.indexOf(report.date);
      if (indexOfReportCount !== -1)
        reportCountArrayBasedOnDate[indexOfReportCount] = report.count;
    });

    return reportCountArrayBasedOnDate;
  };

  const getChartOptions = (maxCount) => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      aspectRatio: 1,
      interaction: {
        mode: "index",
        intersect: false,
      },
      plugins: {
        tooltip: {
          enabled: true,
          position: "nearest",
          padding: 10,
        },
        legend: {
          display: false,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          min: 0,
          max: maxCount && maxCount > 20 ? undefined : 20,
          ticks: {
            stepSize: 5,
          },
          title: {
            display: true,
            text: "No. of Transmission(s)",
          },
        },
        x: {
          ticks: {
            color: "#333333",
            fontSize: 12,
            autoSkip: true,
            maxTicksLimit: 10,
            maxRotation: 30,
            minRotation: 0,
          },
        },
      },
      elements: {
        line: {
          fill: false,
          spanGaps: true,
        },
      },
    };
  };

  return {
    isFetching,
    topUserTransmissionProgressData,
    useStyles,
    getChartOptions,
  };
};

export default useTopTransmissionReportLog;
